import { createContext, useContext, onCleanup } from "solid-js";
import type { ParentComponent } from "solid-js";
import { createStore } from "solid-js/store";
import type { Store } from "solid-js/store";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from "firebase/auth";
import type { User } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { firebase } from "@core/firebase/app";

type RefreshAdminClaimResponse = {
  isAdmin?: boolean;
  reason?: string;
};

export type AdminUser = {
  uid: string;
  email: string | null;
  displayName: string;
  photoURL?: string;
};

export type AuthStatus = "loading" | "signed-out" | "authorizing" | "authorized" | "unauthorized";

export type AuthState = {
  status: AuthStatus;
  user: AdminUser | null;
  error?: string;
};

export type AuthContextValue = {
  state: Store<AuthState>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>();

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: "select_account",
});

const refreshAdminClaim = httpsCallable<unknown, RefreshAdminClaimResponse>(
  firebase.functions,
  "refreshAdminClaim",
);

const extractAdminUser = async (user: User): Promise<AdminUser | null> => {
  let refreshResult: RefreshAdminClaimResponse | undefined;

  try {
    const response = await refreshAdminClaim();
    const data = response?.data;
    if (data && typeof data === "object") {
      refreshResult = data as RefreshAdminClaimResponse;
    }
  } catch (error) {
    console.warn("Failed to refresh admin claim", error);
  }

  let tokenIsAdmin = false;
  try {
    const token = await user.getIdTokenResult(true);
    tokenIsAdmin = token.claims?.isAdmin === true;
  } catch (error) {
    console.warn("Failed to fetch ID token", error);
  }

  const isAdmin = tokenIsAdmin || refreshResult?.isAdmin === true;
  if (!isAdmin) {
    return null;
  }

  if (!tokenIsAdmin) {
    void user.getIdToken(true).catch(() => undefined);
  }

  return {
    uid: user.uid,
    email: user.email ?? null,
    displayName: user.displayName ?? user.email ?? "Administrator",
    photoURL: user.photoURL ?? undefined,
  };
};

export const AuthProvider: ParentComponent = (props) => {
  const [state, setState] = createStore<AuthState>({ status: "loading", user: null });

  const applyUser = async (firebaseUser: User | null) => {
    if (!firebaseUser) {
      setState({ status: "signed-out", user: null, error: undefined });
      return;
    }

    setState("status", "authorizing");
    try {
      const adminUser = await extractAdminUser(firebaseUser);
      if (!adminUser) {
        setState({
          status: "unauthorized",
          user: null,
          error: "Access restricted to HappyTidy administrators.",
        });
        return;
      }

      setState({ status: "authorized", user: adminUser, error: undefined });
    } catch (error) {
      console.error("Failed to apply admin user", error);
      setState({
        status: "signed-out",
        user: null,
        error: "We could not verify your administrator access.",
      });
    }
  };

  const unsubscribe = onAuthStateChanged(
    firebase.auth,
    (firebaseUser) => {
      void applyUser(firebaseUser);
    },
    (error) => {
      console.error("Auth observer error", error);
      setState({ status: "signed-out", user: null, error: "Authentication error" });
    },
  );

  onCleanup(() => unsubscribe());

  const signIn = async () => {
    setState("status", "authorizing");
    try {
      const result = await signInWithPopup(firebase.auth, googleProvider);
      await applyUser(result.user);
    } catch (error: any) {
      console.error("Google sign-in failed", error);
      setState({
        status: "signed-out",
        user: null,
        error: error?.message ?? "Sign-in was cancelled.",
      });
    }
  };

  const signOut = async () => {
    try {
      await refreshAdminClaim();
    } catch (error) {
      console.warn("Failed to refresh admin claim before sign out", error);
    }
    await firebaseSignOut(firebase.auth);
    setState({ status: "signed-out", user: null, error: undefined });
  };

  const refresh = async () => {
    await applyUser(firebase.auth.currentUser);
  };

  const value: AuthContextValue = {
    state,
    signIn,
    signOut,
    refresh,
  };

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};





